import { ArrowLeft, Database, FileText, FolderOpen, Globe, Loader2, Search, Settings, Shield, ShieldCheck, User } from "lucide-react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "../../ui/button";
import StatusIndicator from "../Endpoints/StatusIndicator";
import { Input } from "../../ui/input";
import { Device } from "../interfaces/EndpointInterfaces";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { getUsers } from "../Resources/Api/resourceApi";
import useNotificationStore from "../../../services/notificationStore";
import { fetchResources } from "./Api/tenantuserendpointsapi";
import { Resource } from "./interface";
import TenantResourceRequest from "./TenantResourceRequest";

import { labelMapping } from "../Resources/helper";
import { getOSIcon, transformApiToDevicePerDevice } from "../Endpoints/helper";
import EmptyStateCard from "../EmptyStateCard";
import CardPagination from "../CardPagination";
import "./TenantEndpoints.css";
import { getComputersPerDevice } from "../Endpoints/Api/EndpointApi";
import moment from "moment";
import axios from "axios";
import { NGROK } from "../../../APIs";



const tabs = [
    { id: "app", name: "Application control", icon: Settings },
    { id: "folder", name: "Data access", icon: Database },
    { id: "privilege", name: "Privilege management", icon: Shield },
    { id: "url", name: "URL control", icon: Globe }
];

const ITEMS_PER_PAGE = 10;

export default function TenantUserDeviceDetails() {
    const location = useLocation();
    const { deviceId } = useParams<{ deviceId: string }>();
    const [device, setDevice] = useState<Device | undefined>(location.state?.device);
    const [isDeviceLoading, setIsDeviceLoading] = useState(!location.state?.device);

    let tenant = myLocalStorage.getItem("latestTenant");

    const [activeTab, setActiveTab] = useState("app");
    const [allResources, setAllResources] = useState<Resource[]>([]);
    const [timers, setTimers] = useState<Record<string, number>>({});
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [showAccessDialog, setShowAccessDialog] = useState(false);
    const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
    const [users, setUsers] = useState<any[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<any>();
    const activeTabRef = useRef(activeTab);

    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
    let { email, role, userRole, userId } = useUserStore((state) => state.user);
    const navigate = useNavigate();
    const { userApprovedNotifications } =
        useNotificationStore();

    const isDeviceSSHBased = (device: Device) => {
        if (
            device?.OperatingSystem?.toLowerCase().includes("linux")
        )
            return true;
        else return false;
    };

    const getStatusVal = (resource: Resource) => {
        const statusValue =
            resource.status === "INHERITED"
                ? resource.inheritedGroupStatus
                : resource.status;

        if (statusValue === "DYNAMIC") return "TIMED";
        return statusValue;
    }

    const fetchUsers = useCallback(async (activeDevice: Device) => {
        try {
            let isOwnComputer = activeDevice?.belongedToUser === email;

            const response = await getUsers(
                email,
                tenantName,
                activeDevice.id,
                role,
                isOwnComputer,
                true,
                userRole,
                isDeviceSSHBased(activeDevice),
                userApprovedNotifications
            );

            if (response?.length) {
                const filteredUsers = response.filter((user: any) => user.cn !== "root" && user.cn !== "nobody");
                setUsers(filteredUsers)
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error("Failed to fetch users:", err);
            setUsers([]);
        }
    }, [email, tenantName, role, userRole, userApprovedNotifications]);


    const labelStatus = (mode: string, osmode: string) => {
        let status = mode
        if (mode === "INHERITED_FROM_PROFILE") status = osmode;
        if (mode === "INHERITED_FROM_PROFILE" && osmode === "INHERITED_FROM_TENANT") status = tenant.mode
        return status.toUpperCase();

    }

    const getOperatingModeColor = (mode: string, osMode: string) => {
        let status = labelStatus(mode, osMode);
        switch (status.toLowerCase()) {
            case "enforcing":
                return "destructive";
            case "auditing":
                return "default";
            case "disabled":
                return "secondary";
            default:
                return "secondary";
        }
    };

    useEffect(() => {
        if (!allResources) return;

        const initialTimers: Record<string, number> = {};

        allResources.forEach(resource => {
            const statusVal = getStatusVal(resource);
            const label = labelMapping(statusVal, "");

            if (
                resource.timedAccess?.durationSeconds &&
                (resource.timedAccess && resource.status === "DYNAMIC")
            ) {
                initialTimers[resource.id] = resource.timedAccess.durationSeconds;

            } else if (label && resource.timedAccess?.durationSeconds) {
                initialTimers[resource.id] = resource.timedAccess.durationSeconds;
            }
        });

        setTimers(initialTimers);
    }, [allResources, activeTab]);





    useEffect(() => {
        const interval = setInterval(() => {
            setTimers(prev => {
                const updated = { ...prev };
                let hasChanges = false;
                Object.keys(updated).forEach(resourceId => {
                    if (updated[resourceId] > 0) {
                        updated[resourceId] = updated[resourceId] - 1;
                        hasChanges = true;
                    }
                });

                return hasChanges ? updated : prev;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const loadDevice = async () => {
            if (device?.id) {
                setIsDeviceLoading(false);
                return;
            }
            if (!deviceId || !tenantName || !email || !role) {
                return;
            }
            setIsDeviceLoading(true);
            try {
                const response = await getComputersPerDevice({
                    tenantName,
                    email,
                    role,
                    userId,
                    id: deviceId,
                });
                if (response) {
                    setDevice(transformApiToDevicePerDevice(response));
                }
            } catch (error) {
                console.error("Failed to fetch device details:", error);
            } finally {
                setIsDeviceLoading(false);
            }
        };
        void loadDevice();
    }, [device?.id, deviceId, tenantName, email, role, userId]);

    useEffect(() => {
        if (!device?.id || !email || !role || !tenantName) {
            return;
        }
        void fetchUsers(device);
    }, [device?.id, email, role, tenantName, userApprovedNotifications, fetchUsers]);

    // Connect-access can expire or be revoked while this resource-level page is already open, so
    // poll the backend directly for this computer's current approval status every 5s (independent
    // of whatever cadence other pages refresh the shared notification store on) and bounce back to
    // the endpoints list as soon as it's no longer approved.
    useEffect(() => {
        if (!device?.id || !tenantName || !email || !role) {
            return;
        }

        let cancelled = false;

        const checkApprovalStatus = async () => {
            try {
                const params: Record<string, string> = { role, tenantName };
                if (!email.startsWith("wsadmin")) {
                    params.email = email;
                }
                const response = await axios.get(`${NGROK}/api/access/summary`, { params });
                if (cancelled) {
                    return;
                }

                const onPremAccess = response?.data?.onPremAccess ?? [];
                const approvedAccess = onPremAccess.find(
                    (item: any) =>
                        Number(item.computerId) === Number(device.id) &&
                        item.requestStatus === "APPROVED" &&
                        item.resourceType === "GROUP" &&
                        item.resourceName === "Whiteswan Access"
                );

                let isApproved = false;
                if (approvedAccess) {
                    const nowUTC = moment.utc();
                    if (approvedAccess.schedules && approvedAccess.schedules.length > 0) {
                        isApproved = approvedAccess.schedules.some((schedule: any) =>
                            nowUTC.isBetween(
                                moment.utc(schedule.scheduledStartTime),
                                moment.utc(schedule.scheduledEndTime),
                                null,
                                "[]",
                            ),
                        );
                    } else if (approvedAccess.accessTimeOverAt) {
                        isApproved = nowUTC.isBefore(moment.utc(approvedAccess.accessTimeOverAt));
                    } else {
                        isApproved = true;
                    }
                }

                if (!isApproved) {
                    navigate("/endpoints");
                }
            } catch (error) {
                console.error("Failed to check connect-access approval status:", error);
            }
        };

        void checkApprovalStatus();
        const interval = setInterval(checkApprovalStatus, 5000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [device?.id, tenantName, email, role, navigate]);

    const handleTabChange = (tabId: string) => {
        activeTabRef.current = tabId;
        setActiveTab(tabId);
        setCurrentPage(1);
    };

    const filteredResources = useMemo(() => {
        if (!searchTerm) return allResources;

        return allResources.filter(resource =>
            resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (resource.path && resource.path.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [allResources, searchTerm]);

    const paginatedResources = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredResources.slice(startIndex, endIndex);
    }, [filteredResources, currentPage]);

    const totalPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleRequestAccess = (resource: Resource) => {
        setSelectedResource(resource);
        setShowAccessDialog(true);
    };

    const getResourceIcon = (type: string) => {
        switch (type) {
            case "APP":
                return <FileText aria-hidden="true" />;
            case "FOLDER":
                return <FolderOpen aria-hidden="true" />;
            case "GROUP":
                return <Shield aria-hidden="true" />;
            case "URL":
                return <Globe aria-hidden="true" />;
            default:
                return <FileText aria-hidden="true" />;
        }
    };

    const getDeviceInfo = () => {
        return { name: device?.dNSHostName || device?.name || "", status: "online", os: device?.OperatingSystem || device?.os || "" };
    };



    const deviceInfo = getDeviceInfo();

    const fetchResourceForTab = async (isFirst = false, activeTab: string) => {
        if (!device?.id || !selectedUser?.id) {
            return;
        }
        setLoading(isFirst);

        const data = await fetchResources(activeTab, tenantName, device.id, selectedUser.id);

        if (activeTab !== activeTabRef.current) {
            return;
        }

        const sortedData = Array.isArray(data)
            ? [...data].sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0))
            : [];

        const seenPaths = new Set();
        const distinctData = sortedData.filter((item) => {
            if (seenPaths.has(item.path)) return false;
            seenPaths.add(item.path);
            return true;
        });

        setAllResources(distinctData);
        setLoading(false);
    };


    const formatTime = (seconds: any) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    };


    useEffect(() => {
        if (selectedUser) {
            fetchResourceForTab(true, activeTab);
            const interval = setInterval(() => {
                fetchResourceForTab(false, activeTab);
            }, 10000)
            return () => clearInterval(interval)

        }

    }, [activeTab, selectedUser])

    useEffect(() => {
        if (users.length > 0) {
            setSelectedUser((prev: any) => {
                const stillValid = prev && users.some((user: any) => user.id === prev.id);
                return stillValid ? prev : users[0];
            });
        } else {
            setSelectedUser(undefined);
        }
    }, [users])

    if (isDeviceLoading || !device) {
        return (
            <div className="tenant-endpoints-page tenant-detail-loading">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
        );
    }


    return (
        <div className="tenant-endpoints-page">
            <div className="tenant-endpoints-toolbar">
                <div className="tenant-detail-nav">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => navigate("/endpoints")}
                        className="tenant-detail-back"
                        title="Back to Devices"
                        aria-label="Back to Devices"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div className="tenant-detail-tabs" role="tablist">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className="tenant-detail-tab"
                            >
                                <tab.icon aria-hidden="true" />
                                {tab.name}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="tenant-detail-device">
                    <span className="tenant-device-os">
                        {getOSIcon(deviceInfo.os)}
                    </span>
                    <div className="tenant-device-identity">
                        <h1 className="tenant-device-name">{deviceInfo.name}</h1>
                        <div className="tenant-device-status">
                            <StatusIndicator lastSeen={"offline"} showDot isManualDevice={true} />
                            <span className="tenant-device-os-name">{deviceInfo.os}</span>
                        </div>
                    </div>
                    <span
                        className="tenant-device-mode"
                        data-tone={getOperatingModeColor(device.mode || "", device.profileMode || "")}
                    >
                        {labelStatus(device.mode || "", device.profileMode || "")}
                    </span>
                </div>
            </div>

            <section className="tenant-endpoints-card tenant-detail-card">
                {users.length > 0 ? (
                    <>
                        <aside className="tenant-detail-users">
                            <div className="tenant-detail-heading">User</div>
                            <div className="tenant-detail-users-list">
                                {users.map((user) => (
                                    <button
                                        type="button"
                                        key={user.cn}
                                        className="tenant-detail-user"
                                        aria-pressed={selectedUser?.cn === user.cn}
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <span className="tenant-detail-user-icon">
                                            <User aria-hidden="true" />
                                        </span>
                                        <span className="tenant-detail-user-copy">
                                            <strong>{user.cn}</strong>
                                            <span>Active User</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </aside>
                        <div className="tenant-detail-resources">
                            <div className="tenant-detail-resources-header">
                                <div className="tenant-detail-heading">
                                    Denied Resources
                                    <span className="tenant-access-count">{filteredResources.length}</span>
                                </div>
                                <div className="tenant-endpoints-search" data-wide="true">
                                    <Search aria-hidden="true" />
                                    <Input
                                        placeholder={`Search ${tabs.find(t => t.id === activeTab)?.name.toLowerCase()}...`}
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        data-testid="input-search-resources"
                                    />
                                </div>
                            </div>

                            <div className="tenant-detail-resources-scroll">
                                {isLoading ? (
                                    <div className="tenant-detail-resource-list" aria-hidden="true">
                                        {[...Array(ITEMS_PER_PAGE)].map((_, idx) => (
                                            <div key={idx} className="tenant-detail-resource">
                                                <span className="tenant-skeleton tenant-skeleton-resource" />
                                                <div className="tenant-detail-resource-copy">
                                                    <span className="tenant-skeleton" style={{ width: "40%", height: 14 }} />
                                                    <span className="tenant-skeleton" style={{ width: "65%", height: 11 }} />
                                                </div>
                                                <span className="tenant-skeleton" style={{ width: 96, height: 30 }} />
                                            </div>
                                        ))}
                                    </div>
                                ) : paginatedResources.length ? (
                                    <div className="tenant-detail-resource-list">
                                        {paginatedResources.map((resource, index) => (
                                            <div key={`${resource.type}-${resource.id}-${index}`} className="tenant-detail-resource">
                                                <span className="tenant-detail-resource-icon">
                                                    {getResourceIcon(resource.type)}
                                                </span>
                                                <div className="tenant-detail-resource-copy">
                                                    {resource.type === "APP" && resource.path ? (
                                                        <>
                                                            <strong>{resource.name}</strong>
                                                            <code>{resource.path}</code>
                                                        </>
                                                    ) : (
                                                        <strong>{resource.path || resource.name}</strong>
                                                    )}
                                                </div>
                                                <div className="tenant-detail-resource-actions">
                                                    <span
                                                        className="tenant-device-mode"
                                                        data-tone={
                                                            timers[resource.id] > 0 ? "secondary"
                                                                : labelMapping(getStatusVal(resource), "") === "ALLOWED"
                                                                    ? "default"
                                                                    : labelMapping(getStatusVal(resource), "") === "DENIED"
                                                                        ? "destructive"
                                                                        : labelMapping(getStatusVal(resource), "") === "TIMED"
                                                                            ? "secondary"
                                                                            : "default"
                                                        }
                                                    >
                                                        {timers[resource.id] > 0 ? "TIMED" : labelMapping(getStatusVal(resource), "")}
                                                    </span>

                                                    {timers[resource.id] > 0 && (
                                                        <span className="tenant-device-timer">
                                                            {formatTime(timers[resource.id])} left
                                                        </span>
                                                    )}

                                                    {(
                                                        labelMapping(getStatusVal(resource), "") === "DENIED" ||
                                                        (
                                                            labelMapping(getStatusVal(resource), "") === "TIMED" &&
                                                            (resource?.timedAccess?.durationSeconds ?? 0) === 0
                                                        )
                                                    ) && (
                                                            <Button
                                                                size="sm"
                                                                className="tenant-detail-request"
                                                                onClick={() => handleRequestAccess(resource)}
                                                            >
                                                                <ShieldCheck className="w-4 h-4" />
                                                                Request Access
                                                            </Button>
                                                        )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <EmptyStateCard
                                        compact
                                        embedded
                                        icon={Shield}
                                        title="No resources found"
                                        description="No resources are available on this device for your role."
                                        className="tenant-endpoints-empty"
                                    />
                                )}
                            </div>

                            {totalPages > 1 && (
                                <div className="tenant-endpoints-footer">
                                    <CardPagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        label="Resources pagination"
                                    />
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <EmptyStateCard
                        compact
                        embedded
                        icon={User}
                        title="No users available"
                        description="No users are available on this device for your role."
                        className="tenant-endpoints-empty tenant-detail-empty"
                    />
                )}
            </section>

            {showAccessDialog && selectedResource && (
                <TenantResourceRequest
                    open={showAccessDialog}
                    onOpenChange={setShowAccessDialog}
                    resourceName={selectedResource?.path || selectedResource?.name || "Resource"}
                    resourceType={selectedResource?.type || "Resource"}
                    deviceName={device.dNSHostName}
                    userId={selectedUser.id}
                    user={selectedUser}
                    resourceId={selectedResource?.id}
                    device={device}
                    publisher={selectedResource.publisher}
                />
            )}
        </div>
    );
}
